import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ConversationLogger, { warmSpeech } from '../components/home/ConversationLogger';
import DailyInterview from '../components/home/DailyInterview';
import LoggedToday from '../components/log/LoggedToday';
import { canConverse } from '../components/home/VoiceConversation';
import { localToday } from '../utils/date';

// Log (2026-10 restyle). Say what you ate or did; we sort it out. The capture
// card sits at the top, the quick questions under it, then your one-tap
// routines, then everything logged today. Talking is still a session: the
// field, the mic and the examples all open the conversation full-screen.

const EXAMPLES = ['Two eggs and toast', 'Walked 30 minutes', 'Coffee at 7', 'Took my magnesium'];

const MicIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[22px] w-[22px]" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" />
  </svg>
);
const UpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[22px] w-[22px]" aria-hidden="true">
    <path d="M12 19V5" /><path d="M5 12l7-7 7 7" />
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
    <path d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const dayWord = (iso, today) => {
  if (iso === today) return 'Today';
  const d = new Date(`${iso}T00:00:00`);
  const t = new Date(`${today}T00:00:00`);
  if (Math.round((t - d) / 86400000) === 1) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const LogPage = () => {
  const today = useMemo(() => localToday(), []);
  const [date, setDate] = useState(today);
  const [refreshKey, setRefreshKey] = useState(0);
  // The open conversation: null | { mode: 'type'|'auto'|'walkie', text }.
  const [chat, setChat] = useState(null);
  const bump = () => setRefreshKey((k) => k + 1);

  const openType = (text = '') => setChat({ mode: 'type', text });
  // Voice must warm speech synthesis inside THIS tap (mobile autoplay policy).
  const openVoice = (mode) => { warmSpeech(); setChat({ mode, text: '' }); };

  return (
    <div className="mx-auto max-w-[1080px] px-4 sm:px-6 lg:px-10">
      <div className="mb-[22px] flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[1.75rem] font-medium leading-[1.15] tracking-[-0.02em]">Log</h1>
          <p className="mt-1 max-w-[40em] text-fg-3">Say what you ate or did. We sort it out.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="relative inline-flex cursor-pointer items-center gap-2 rounded-full border border-line-strong px-3.5 py-2 text-sm font-medium text-fg-2 hover:border-fg-3 hover:text-fg">
            {dayWord(date, today)}
            <span className="h-1.5 w-1.5 -translate-y-px rotate-45 border-b-[1.5px] border-r-[1.5px] border-current" aria-hidden="true" />
            <input
              type="date"
              value={date}
              max={today}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              aria-label="Which day to log"
              className="absolute inset-0 cursor-pointer opacity-0"
            />
          </label>
          <Link to="/data" className="text-sm font-medium text-fg-2 hover:text-fg">History →</Link>
        </div>
      </div>

      {/* Capture: the field, the mic, and a few examples of the kind of thing to say. */}
      <div className="rounded-[18px] border border-line bg-raised p-4 sm:p-5">
        <div className="flex min-h-[60px] items-center gap-3 rounded-full border border-line-strong bg-canvas py-1.5 pl-5 pr-1.5">
          <button type="button" onClick={() => openType()} className="min-w-0 flex-1 truncate bg-transparent p-0 text-left text-[1.1rem] text-fg-3 hover:text-fg-2">
            What did you eat, or do?
          </button>
          {canConverse && (
            <button type="button" onClick={() => openVoice('walkie')} aria-label="Talk"
              className="grid h-12 w-12 flex-none place-items-center rounded-full border border-line-strong bg-transparent text-fg hover:bg-elev">
              <MicIcon />
            </button>
          )}
          <button type="button" onClick={() => openType()} aria-label="Type it"
            className="grid h-12 w-12 flex-none place-items-center rounded-full bg-cta text-cta-fg hover:bg-cta-hover">
            <UpIcon />
          </button>
        </div>
        <div className="mt-3.5 flex flex-wrap items-center gap-2">
          <em className="mr-1 text-sm not-italic text-fg-3">Like:</em>
          {EXAMPLES.map((ex) => (
            <button key={ex} type="button" onClick={() => openType(ex)}
              className="rounded-full border border-line-strong bg-transparent px-3.5 py-2 text-sm text-fg-2 hover:bg-elev hover:text-fg">
              {ex}
            </button>
          ))}
        </div>
      </div>

      <DailyInterview refreshKey={refreshKey} defaultDate={date} onLogged={bump} />
      <LoggedToday date={date} refreshKey={refreshKey} />

      {/* The conversation, as its own full-screen surface above the app chrome. */}
      {chat && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-canvas text-fg">
          <div className="flex shrink-0 items-center justify-between border-b border-line px-4 py-2.5">
            <span className="text-base font-medium">Log it · {dayWord(date, today).toLowerCase()}</span>
            <button type="button" onClick={() => setChat(null)} aria-label="Close"
              className="grid h-9 w-9 place-items-center rounded-full bg-transparent text-fg-2 hover:bg-elev hover:text-fg">
              <CloseIcon />
            </button>
          </div>
          <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col p-3">
            <ConversationLogger
              key={`${chat.mode}-${chat.text}`}
              fill
              startIn={chat.mode}
              initialText={chat.text}
              defaultDate={date}
              onEntrySaved={bump}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LogPage;
